import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import type { AgGridReact } from 'ag-grid-react';
import type { RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { Alert, CircularProgress, Box } from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
} from '@mui/icons-material';
import { useGetSftpTenantConfigsQuery, SortEnumType } from '@/services/graphql';
import AgGrid from '@/components/grid/AgGrid';
import type { GridRibbonControl } from '@/components/grid/AgGridRibbon';
import sftpTenantConfigColDef from './columnDefs/sftpTenantConfigColDef';
import GridErrorBoundary from '@/components/grid/GridErrorBoundary';
import type { SftpTenantConfigDocument } from '@/services/graphql';

type SftpTenantConfig = SftpTenantConfigDocument;

interface SftpTenantConfigGridProps {
  height?: string;
  searchTerm?: string;
  onConfigClick?: (config: SftpTenantConfig) => void;
  onCreateConfig?: () => void;
  onEditConfig?: (config: SftpTenantConfig) => void;
  onDeleteConfig?: (config: SftpTenantConfig) => void;
  onCloneConfig?: (config: SftpTenantConfig) => void;
}

export const SftpTenantConfigGrid: React.FC<SftpTenantConfigGridProps> = ({ 
  height = '600px',
  searchTerm,
  onConfigClick,
  onCreateConfig,
  onEditConfig,
  onDeleteConfig,
  onCloneConfig,
}) => {
  const gridRef = useRef<AgGridReact<SftpTenantConfig>>(null);
  const [selectedRow, setSelectedRow] = useState<SftpTenantConfig | null>(null);

  // Build where clause for search filtering
  const whereClause = useMemo(() => {
    if (!searchTerm) return undefined;
    
    return {
      or: [
        { configId: { contains: searchTerm } },
        { tenantId: { contains: searchTerm } },
        { configName: { contains: searchTerm } },
        { podId: { contains: searchTerm } },
      ]
    };
  }, [searchTerm]);

  // Query for tenant configs data
  const { data, isLoading, error, refetch } = useGetSftpTenantConfigsQuery({
    first: 100,
    where: whereClause,
    order: [{ configName: SortEnumType.Asc }],
  });

  // Prepare row data (moved up before useEffect)
  const rowData = (data?.sftpTenantConfigs?.edges?.map(edge => edge.node) || []) as SftpTenantConfig[];

  // Update selected row when data changes
  useEffect(() => {
    if (selectedRow && rowData.length > 0) {
      // Find the updated version of the selected row
      const updatedRow = rowData.find(row => 
        row.configId === selectedRow.configId && 
        row.tenantId === selectedRow.tenantId
      );
      if (updatedRow && updatedRow.enabled !== selectedRow.enabled) {
        setSelectedRow(updatedRow);
      }
    }
  }, [rowData, selectedRow]);


  // Refresh handler wired into AgGrid's built-in onRefresh control
  const handleRefresh = useCallback(() => {
    refetch();
    // Also clear any selection when refreshing
    gridRef.current?.api?.deselectAll();
    setSelectedRow(null);
  }, [refetch]);

  // Custom ribbon controls
  const ribbonControls = useMemo<GridRibbonControl[]>(() => {
    const controls: GridRibbonControl[] = [
      {
        id: 'export',
        label: 'Export to CSV',
        icon: <DownloadIcon />,
        onClick: () => {
          const gridApi = gridRef.current?.api;
          if (gridApi) {
            gridApi.exportDataAsCsv({
              fileName: `sftp-tenant-configs-${new Date().toISOString().split('T')[0]}.csv`,
            });
          }
        },
      },
    ];

    if (onCreateConfig) {
      controls.unshift({
        id: 'create',
        label: 'Add Tenant Config',
        icon: <AddIcon />,
        onClick: onCreateConfig,
        variant: 'chip',
      });
    }

    // Add row-specific actions when a row is selected
    if (selectedRow) {
      if (onEditConfig) {
        controls.push({
          id: 'edit',
          label: 'Edit',
          icon: <EditIcon />,
          onClick: () => onEditConfig(selectedRow),
          variant: 'chip',
        });
      }

      // Add clone button
      if (onCloneConfig) {
        controls.push({
          id: 'clone',
          label: 'Clone',
          icon: <CloneIcon />,
          onClick: () => onCloneConfig(selectedRow),
          variant: 'chip',
        });
      }

      // Add delete button
      if (onDeleteConfig) {
        controls.push({
          id: 'delete',
          label: 'Delete',
          icon: <DeleteIcon />,
          onClick: () => onDeleteConfig(selectedRow),
          variant: 'chip',
        });
      }

    }

    return controls;
  }, [onCreateConfig, selectedRow, onEditConfig, onDeleteConfig, onCloneConfig]);

  // Row click handler
  const handleRowClicked = (event: RowClickedEvent<SftpTenantConfig>) => {
    if (event.data) {
      setSelectedRow(event.data);
      if (onConfigClick) {
        onConfigClick(event.data);
      }
    }
  };

  // Selection changed handler
  const handleSelectionChanged = () => {
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;
    
    const selectedRows = gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      setSelectedRow(selectedRows[0]);
    } else {
      setSelectedRow(null);
    }
  };

  // Row double-click handler
  const handleRowDoubleClicked = (event: RowDoubleClickedEvent<SftpTenantConfig>) => {
    if (event.data && onEditConfig) {
      onEditConfig(event.data);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load SFTP tenant configurations
      </Alert>
    );
  }

  // Row ID function for grid state persistence
  const getRowId = (params: { data: SftpTenantConfig }) => 
    `${params.data.tenantId}-${params.data.configId}`;

  return (
    <GridErrorBoundary gridName="SFTP Tenant Config Grid">
      <AgGrid<SftpTenantConfig>
        gridRef={gridRef}
        gridDataKey="sftp-tenant-configs"
        getRowId={getRowId}
        rowData={rowData}
        height={height}
        gridRibbonControls={ribbonControls}
        onRefresh={handleRefresh}
        refreshing={isLoading}
        gridOptions={{
          columnDefs: sftpTenantConfigColDef(),
          onRowClicked: handleRowClicked,
          onRowDoubleClicked: handleRowDoubleClicked,
          onSelectionChanged: handleSelectionChanged,
          rowSelection: {
      mode: 'singleRow',
      checkboxes: true,
      enableClickSelection: true
    },
          rowClass: onEditConfig ? 'cursor-pointer' : undefined,
          pagination: true,
          paginationPageSize: 50,
          animateRows: true,
          getRowStyle: (params) => {
            // Highlight selected row
            if (selectedRow && params.data?.configId === selectedRow.configId) {
              return { 
                backgroundColor: '#e3f2fd',
              };
            }
            return undefined;
          },
        }}
      />
    </GridErrorBoundary>
  );
};